import { NUCLEOTIDE_CODES } from "@/lib/tool-kit/biology-genetics/iupac";
import {
  ENZYME_TABLE,
  POPULAR_ENZYMES,
} from "@/lib/tool-kit/biology-genetics/restriction-enzymes";

/**
 * Restriction-site search and digestion.
 *
 * An enzyme is a recognition pattern (IUPAC codes) plus two cut positions measured in bases
 * from the start of the pattern: `top` on the strand the pattern is written on, `bottom` on
 * the opposite strand, both as "the cut falls after this many bases" (negative = upstream of
 * the site). This is the convention of the EMBOSS/REBASE `emboss_e` file; the unit tests pin
 * a sample of parsed enzymes to those REBASE values.
 *
 * Fragment sizes use the top-strand cut positions, as NEBcutter and Biopython do.
 */

export interface Enzyme {
  name: string;
  /** The recognition site in REBASE notation, e.g. "G^AATTC" or "GGTCTC(1/5)". */
  notation: string;
  pattern: string;
  top: number;
  bottom: number;
  /** The site reads the same on both strands, so one orientation covers both. */
  palindromic: boolean;
  /** Longest stretch of sequence the site and its cuts need, for circular wrap-around. */
  span: number;
}

export type OverhangType = "blunt" | "5prime" | "3prime";

export interface Overhang {
  type: OverhangType;
  length: number;
}

const NOTATION = /^([ACGTRYSWKMBDHVN^]+)(?:\((-?\d+)\/(-?\d+)\))?$/;
const LEADING_N = /^N+/;
const TRAILING_N = /N+$/;

/** Parse REBASE notation into pattern and cut positions; null if it is not valid. */
export function parseSite(
  notation: string
): { pattern: string; top: number; bottom: number } | null {
  const match = notation.trim().toUpperCase().match(NOTATION);
  if (!match) {
    return null;
  }
  const [, body, first, second] = match;
  const caret = body.indexOf("^");
  if (caret !== -1) {
    if (first !== undefined || body.indexOf("^", caret + 1) !== -1) {
      return null;
    }
    const raw = body.replace("^", "");
    const lead = raw.match(LEADING_N)?.[0].length ?? 0;
    const pattern = raw.replace(LEADING_N, "").replace(TRAILING_N, "");
    if (pattern === "") {
      return null;
    }
    const top = caret - lead;
    return { pattern, top, bottom: pattern.length - top };
  }
  if (first === undefined || second === undefined) {
    return null;
  }
  const length = body.length;
  return {
    pattern: body,
    top: length + Number(first),
    bottom: length + Number(second),
  };
}

const COMPLEMENT_CODE: Readonly<Record<string, string>> = {
  A: "T",
  C: "G",
  G: "C",
  T: "A",
  R: "Y",
  Y: "R",
  K: "M",
  M: "K",
  B: "V",
  V: "B",
  D: "H",
  H: "D",
  S: "S",
  W: "W",
  N: "N",
};

function patternReverseComplement(pattern: string): string {
  let out = "";
  for (let i = pattern.length - 1; i >= 0; i--) {
    out += COMPLEMENT_CODE[pattern[i]];
  }
  return out;
}

export function makeEnzyme(name: string, notation: string): Enzyme | null {
  const parsed = parseSite(notation);
  if (!parsed) {
    return null;
  }
  const { pattern, top, bottom } = parsed;
  const span = Math.max(pattern.length, top, bottom) - Math.min(0, top, bottom);
  return {
    name,
    notation: notation.trim().toUpperCase(),
    pattern,
    top,
    bottom,
    palindromic: patternReverseComplement(pattern) === pattern,
    span,
  };
}

function buildAll(): Enzyme[] {
  const list: Enzyme[] = [];
  for (const [name, notation] of ENZYME_TABLE) {
    const enzyme = makeEnzyme(name, notation);
    if (enzyme) {
      list.push(enzyme);
    }
  }
  return list;
}

export const ALL_ENZYMES: readonly Enzyme[] = buildAll();

export const POPULAR: readonly Enzyme[] = POPULAR_ENZYMES.flatMap((name) => {
  const found = ALL_ENZYMES.find((enzyme) => enzyme.name === name);
  return found ? [found] : [];
});

export function overhangOf(enzyme: Enzyme): Overhang {
  const length = Math.abs(enzyme.top - enzyme.bottom);
  if (length === 0) {
    return { type: "blunt", length: 0 };
  }
  return { type: enzyme.top < enzyme.bottom ? "5prime" : "3prime", length };
}

const CLASS: Readonly<Record<string, string>> = Object.fromEntries(
  Object.keys(NUCLEOTIDE_CODES)
    .filter((code) => code !== "U")
    .map((code) => [
      code,
      NUCLEOTIDE_CODES[code].length === 1
        ? NUCLEOTIDE_CODES[code]
        : `[${NUCLEOTIDE_CODES[code]}]`,
    ])
);

function toRegex(pattern: string): RegExp {
  const body = [...pattern].map((code) => CLASS[code]).join("");
  return new RegExp(`(?=${body})`, "g");
}

export interface Hit {
  enzyme: string;
  strand: "+" | "-";
  /** 0-based start of the recognition site on the top strand. */
  start: number;
  /** 0-based end (exclusive). */
  end: number;
  /** Bases to the left of the top-strand cut, i.e. the cut falls after this 1-based base. */
  topCut: number;
  /** The same for the bottom-strand cut, in top-strand coordinates. */
  bottomCut: number;
  /** The recognised bases as they appear on the top strand. */
  matched: string;
}

function wrap(value: number, length: number): number {
  return ((value % length) + length) % length;
}

function inside(
  hit: { topCut: number; bottomCut: number },
  length: number
): boolean {
  return (
    Math.min(hit.topCut, hit.bottomCut) > 0 &&
    Math.max(hit.topCut, hit.bottomCut) < length
  );
}

function makeHit(
  enzyme: Enzyme,
  strand: "+" | "-",
  start: number,
  haystack: string,
  length: number,
  circular: boolean
): Hit | null {
  const width = enzyme.pattern.length;
  const topCut =
    strand === "+" ? start + enzyme.top : start + width - enzyme.bottom;
  const bottomCut =
    strand === "+" ? start + enzyme.bottom : start + width - enzyme.top;
  const cuts = circular
    ? {
        topCut: wrap(topCut, length) || length,
        bottomCut: wrap(bottomCut, length) || length,
      }
    : { topCut, bottomCut };
  if (!(circular || inside(cuts, length))) {
    return null;
  }
  return {
    enzyme: enzyme.name,
    strand,
    start,
    end: start + width,
    matched: haystack.slice(start, start + width),
    ...cuts,
  };
}

/** All sites of one enzyme in `sequence` (upper-case, T not U). */
export function findSites(
  sequence: string,
  enzyme: Enzyme,
  circular: boolean
): Hit[] {
  const length = sequence.length;
  const width = enzyme.pattern.length;
  if (length < width) {
    return [];
  }
  const haystack = circular
    ? sequence + sequence.slice(0, width - 1)
    : sequence;
  const orientations: { strand: "+" | "-"; pattern: string }[] = [
    { strand: "+", pattern: enzyme.pattern },
  ];
  if (!enzyme.palindromic) {
    orientations.push({
      strand: "-",
      pattern: patternReverseComplement(enzyme.pattern),
    });
  }
  const hits: Hit[] = [];
  for (const { strand, pattern } of orientations) {
    for (const match of haystack.matchAll(toRegex(pattern))) {
      const start = match.index ?? 0;
      const hit =
        start < length
          ? makeHit(enzyme, strand, start, haystack, length, circular)
          : null;
      if (hit) {
        hits.push(hit);
      }
    }
  }
  return hits.sort((a, b) => a.topCut - b.topCut);
}

export interface Fragment {
  /** 1-based first base. */
  start: number;
  /** 1-based last base (may be less than start when a circular fragment wraps). */
  end: number;
  length: number;
}

/** Fragments from top-strand cut positions (each "after base N"). */
export function fragments(
  cuts: readonly number[],
  length: number,
  circular: boolean
): Fragment[] {
  const sorted = [...new Set(cuts)]
    .filter((cut) => cut > 0 && cut <= length)
    .sort((a, b) => a - b);
  if (sorted.length === 0) {
    return [{ start: 1, end: length, length }];
  }
  const out: Fragment[] = [];
  if (circular) {
    sorted.forEach((cut, index) => {
      const last = index === sorted.length - 1;
      const next = sorted[last ? 0 : index + 1];
      out.push({
        start: (cut % length) + 1,
        end: next,
        length: last ? length - cut + sorted[0] : next - cut,
      });
    });
    return out;
  }
  let previous = 0;
  for (const cut of sorted) {
    if (cut < length) {
      out.push({ start: previous + 1, end: cut, length: cut - previous });
      previous = cut;
    }
  }
  out.push({ start: previous + 1, end: length, length: length - previous });
  return out;
}

export interface EnzymeResult {
  enzyme: Enzyme;
  hits: Hit[];
}

export function searchAll(
  sequence: string,
  enzymes: readonly Enzyme[],
  circular: boolean
): EnzymeResult[] {
  return enzymes.map((enzyme) => ({
    enzyme,
    hits: findSites(sequence, enzyme, circular),
  }));
}
